import { API_BASE } from '../config/apiBase';
import { createContext, useContext, useState, useEffect } from 'react';
import axios from 'axios';
import i18n from '../i18n';

const AuthContext = createContext(null);

const API = API_BASE;

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(localStorage.getItem('token'));
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (token) {
      axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
      fetchUser();
    } else {
      setLoading(false);
    }
  }, [token]);

  // Sync language when user changes
  const syncUserLanguage = (userData) => {
    if (userData) {
      // For trainees, use their training_language; for others, use preferred_language or localStorage
      const userLang = userData.training_language || userData.preferred_language || localStorage.getItem('language') || 'en';
      if (i18n.language !== userLang) {
        i18n.changeLanguage(userLang);
        localStorage.setItem('language', userLang);
      }
    }
  };

  const fetchUser = async () => {
    try {
      // Try new secure endpoint first, fallback to legacy
      try {
        const response = await axios.get(`${API}/auth/session`);
        if (response.data.valid && response.data.user) {
          setUser(response.data.user);
          syncUserLanguage(response.data.user);
          return;
        }
      } catch (e) {
        // Check if session expired
        if (e.response?.status === 401) {
          const detail = e.response?.data?.detail || '';
          if (detail.includes('expirada') || detail.includes('expired')) {
            console.warn('Session expired - user needs to re-authenticate');
            // Show toast notification if available
            if (window.showSessionExpiredToast) {
              window.showSessionExpiredToast();
            }
          }
        }
        // Fallback to legacy endpoint
      }
      
      const response = await axios.get(`${API}/auth/me`);
      setUser(response.data);
      syncUserLanguage(response.data);
    } catch (error) {
      console.error('Error fetching user:', error);
      // Clear session but don't force redirect - let components handle it
      logout(true); // silent logout
    } finally {
      setLoading(false);
    }
  };

  // Legacy login with password (deprecated)
  const login = async (email, password) => {
    const response = await axios.post(`${API}/auth/token`, { email, password });
    const { access_token, user: userData } = response.data;
    
    localStorage.setItem('token', access_token);
    axios.defaults.headers.common['Authorization'] = `Bearer ${access_token}`;
    setToken(access_token);
    setUser(userData);
    
    return userData;
  };

  // New OTP-based login
  const loginWithToken = async (accessToken, userData) => {
    localStorage.setItem('token', accessToken);
    axios.defaults.headers.common['Authorization'] = `Bearer ${accessToken}`;
    setToken(accessToken);
    setUser(userData);
    syncUserLanguage(userData);
    return userData;
  };

  // Update user's preferred language
  const updateLanguage = async (newLang) => {
    try {
      await axios.put(`${API}/users/me/language`, { language: newLang });
      // Update local user state
      setUser(prev => prev ? { ...prev, preferred_language: newLang, training_language: newLang } : null);
      localStorage.setItem('language', newLang);
      i18n.changeLanguage(newLang);
    } catch (error) {
      console.error('Error updating language preference:', error);
      // Still update locally even if API fails
      localStorage.setItem('language', newLang);
      i18n.changeLanguage(newLang);
    }
  };

  const logout = async (silent = false) => {
    // Call logout endpoint to log the event (only if not silent)
    try {
      if (token && !silent) {
        await axios.post(`${API}/auth/logout`);
      }
    } catch (e) {
      // Ignore errors on logout
    }
    
    localStorage.removeItem('token');
    delete axios.defaults.headers.common['Authorization'];
    setToken(null);
    setUser(null);
  };

  const isAdmin = () => user?.role === 'admin';
  const isManager = () => user?.role === 'manager';
  const isAnalyst = () => user?.role === 'analyst';
  const isTrainee = () => user?.role === 'trainee';
  const isManagerOrAdmin = () => user?.role === 'manager' || user?.role === 'admin';
  const canAccessCredit = () => user?.role !== 'trainee' || user?.can_access_credit;
  const canAccessTraining = () => user?.role === 'trainee' || user?.role === 'admin';

  return (
    <AuthContext.Provider value={{
      user,
      token,
      loading,
      login,
      loginWithToken,
      logout,
      updateLanguage,
      isAdmin,
      isManager,
      isAnalyst,
      isTrainee,
      isManagerOrAdmin,
      canAccessCredit,
      canAccessTraining
    }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
